import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, Sparkles } from 'lucide-react';
import { PATH_ROOT } from '@/routes/paths';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Person as PersonIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Button } from '@/components/ui/Button';
import { restartDemoOnboarding } from '@/views/demo-ari/welcome/demoWelcomeStorage';
import { CreateOrgModal } from '@/components/organization';
import { useDemoOrgData } from '@/hooks/organization/useDemoOrgData';
import { notifyFeature } from '@/utils/demoNotifications';
import { useOrganizations } from 'src/hooks/organization';
import { createOrgApi } from 'src/api/organization';
import { useDemoOrg } from 'src/contexts/DemoOrgContext';
import { useOrg } from 'src/contexts/OrgContext';
import { useProfile } from 'src/hooks/profile/useProfile';
import { getUnknownErrorMessage } from 'src/utils/getUnknownErrorMessage';
import { getOrgTypeLabel, getRoleLabel } from 'src/utils/organizationUtils';

interface OrganizationsListProps {
  isDemo?: boolean;
  variant?: 'selector' | 'management';
}

const OrganizationsList: React.FC<OrganizationsListProps> = ({
  isDemo = false,
  variant = 'selector',
}) => {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const demoOrgContext = useDemoOrg();
  const { setSelectedOrgId } = useOrg();
  const { profile } = useProfile();
  const { organizations: demoOrganizations, getCurrentUserRoleInOrg } = useDemoOrgData();

  const {
    organizations: apiOrganizations,
    isLoading,
    isValidating,
    error,
    refreshOrganizations,
  } = useOrganizations();

  const allOrganizations = isDemo ? demoOrganizations : apiOrganizations;
  const organizations = allOrganizations.filter((org) => org.orgId !== profile?.userId);
  const showLoading = !isDemo && isLoading;
  const showError = !isDemo && error != null;
  const organizationsErrorMessage = showError
    ? getUnknownErrorMessage(error, 'Failed to load organizations')
    : null;

  const handleSelectPersonal = () => {
    if (isDemo) {
      demoOrgContext?.setPersonalMode();
      navigate(PATH_ROOT.demo.dashboard);
    } else {
      setSelectedOrgId(profile?.userId);
      navigate(PATH_ROOT.main.dashboard);
    }
  };

  const handleCreateOrgSubmit = async (data: {
    name: string;
    type: string;
    description?: string;
    billingEmail: string;
  }) => {
    const result = await createOrgApi(data);

    if (isDemo) {
      navigate(PATH_ROOT.demo.organizationManagement(result.org.orgId));
    } else {
      setSelectedOrgId(result.org.orgId);
      navigate(PATH_ROOT.organization.dashboard);
    }
  };

  const handleCreateOrg = () => {
    if (isDemo) {
      notifyFeature('Create Organization', isDemo);
    } else {
      setShowCreateModal(true);
    }
  };

  const handleReplayOnboarding = async () => {
    await restartDemoOnboarding();
    navigate(PATH_ROOT.demo.welcome);
  };

  const handleViewOrg = (orgId: string) => {
    if (isDemo) {
      const selectedOrg = demoOrganizations.find((org) => org.orgId === orgId);
      if (selectedOrg && demoOrgContext) {
        demoOrgContext.setSelectedOrg(selectedOrg);
      }

      navigate(
        variant === 'management'
          ? PATH_ROOT.demo.organizationManagement(orgId)
          : PATH_ROOT.demo.dashboard,
      );
    } else {
      setSelectedOrgId(orgId);
      navigate(
        variant === 'management'
          ? PATH_ROOT.organization.management
          : PATH_ROOT.organization.dashboard,
      );
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-10">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          {variant === 'management' ? 'My Organizations' : 'Workspaces'}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {variant === 'management'
            ? 'Manage your organizations and create new ones'
            : 'Select a workspace to get started'}
        </p>
      </div>

      {showLoading && (
        <div className="px-5 py-16 text-center">
          <div className="animate-pulse text-muted-foreground">
            Loading organizations...
          </div>
        </div>
      )}

      {showError ? (
        <div className="px-5 py-16 text-center">
          <div className="mb-4 text-red-400" role="alert">
            {organizationsErrorMessage}
          </div>
          <Button
            type="button"
            onClick={() => {
              void refreshOrganizations();
            }}
            disabled={isValidating}
            variant="outline"
            size="default"
          >
            {isValidating ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      ) : null}

      {!showLoading && !showError && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {isDemo && variant === 'selector' ? (
            <>
            <button
              type="button"
              className="group relative cursor-pointer rounded-xl border border-primary/30 bg-neutral-900 p-6 text-left transition-all duration-150 hover:border-primary/50 hover:bg-neutral-800/80"
              onClick={() => {
                void handleReplayOnboarding();
              }}
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/15">
                  <Sparkles className="size-5 text-primary" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold text-white">Replay onboarding</h3>
                  <p className="mt-1 text-sm text-white/40">
                    Walk through sign-in, workspace setup, and inbox ingest again
                  </p>
                </div>
                <ChevronRightIcon
                  className="mt-1 shrink-0 text-white/20 transition-colors group-hover:text-white/50"
                  sx={{ fontSize: 20 }}
                />
              </div>
            </button>
            <button
              type="button"
              className="group relative cursor-pointer rounded-xl border border-info/30 bg-neutral-900 p-6 text-left transition-all duration-150 hover:border-info/50 hover:bg-neutral-800/80"
              onClick={() => navigate(PATH_ROOT.demo.dealComparison)}
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-info/25 bg-info/15">
                  <Scale className="size-5 text-info" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold text-white">Deal comparison</h3>
                  <p className="mt-1 text-sm text-white/40">
                    Compare competing offers — Travis Scott × 3 brands demo
                  </p>
                </div>
                <ChevronRightIcon
                  className="mt-1 shrink-0 text-white/20 transition-colors group-hover:text-white/50"
                  sx={{ fontSize: 20 }}
                />
              </div>
            </button>
            </>
          ) : null}

          <div
            className="group relative cursor-pointer rounded-xl border border-white/[0.08] bg-neutral-900 p-6 transition-all duration-150 hover:border-blue-500/40 hover:bg-neutral-800/80"
            onClick={handleSelectPersonal}
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg border border-blue-500/20 bg-blue-500/15">
                <PersonIcon className="text-blue-400" sx={{ fontSize: 24 }} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-white">Personal Workspace</h3>
                <p className="mt-1 text-sm text-white/40">Individual contracts and documents</p>
              </div>
              <ChevronRightIcon
                className="mt-1 flex-shrink-0 text-white/20 transition-colors group-hover:text-white/50"
                sx={{ fontSize: 20 }}
              />
            </div>
          </div>

          {organizations.map((org) => {
            const userRole = isDemo ? getCurrentUserRoleInOrg(org.orgId)?.role : org.userRole;
            const customRoleName = isDemo ? getCurrentUserRoleInOrg(org.orgId)?.customRoleName : undefined;
            const colors = (org as { colors?: { primary: string; secondary: string; accent: string } }).colors;

            return (
              <div
                key={org.orgId}
                className="group relative cursor-pointer rounded-xl border border-white/[0.08] bg-neutral-900 p-6 transition-all duration-150 hover:border-white/20 hover:bg-neutral-800/80"
                onClick={() => handleViewOrg(org.orgId)}
                onMouseEnter={(event) => {
                  if (colors) {
                    event.currentTarget.style.borderColor = `${colors.primary}50`;
                  }
                }}
                onMouseLeave={(event) => {
                  if (colors) {
                    event.currentTarget.style.borderColor = '';
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border"
                    style={colors ? {
                      backgroundColor: `${colors.primary}20`,
                      borderColor: `${colors.primary}30`,
                    } : {
                      backgroundColor: 'rgba(147, 51, 234, 0.15)',
                      borderColor: 'rgba(147, 51, 234, 0.2)',
                    }}
                  >
                    {org.logoUrl ? (
                      <img src={org.logoUrl} alt={org.name} className="h-8 w-8 object-contain" />
                    ) : (
                      <span
                        className="text-sm font-bold"
                        style={{ color: colors?.primary ?? '#a855f7' }}
                      >
                        {org.name.substring(0, 2)}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-base font-semibold text-white">{org.name}</h3>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <span className="text-white/40">{getOrgTypeLabel(org.type)}</span>
                      {org.memberCount !== undefined && (
                        <>
                          <span className="text-white/15">·</span>
                          <span className="text-white/40">{org.memberCount} members</span>
                        </>
                      )}
                    </div>
                    {userRole && (
                      <span
                        className="mt-2 inline-block rounded-md px-2 py-0.5 text-xs font-medium"
                        style={colors ? {
                          backgroundColor: `${colors.primary}15`,
                          color: `${colors.primary}cc`,
                        } : {
                          backgroundColor: 'rgba(147, 51, 234, 0.1)',
                          color: 'rgba(168, 85, 247, 0.8)',
                        }}
                      >
                        {getRoleLabel(userRole, customRoleName)}
                      </span>
                    )}
                  </div>

                  <ChevronRightIcon
                    className="mt-1 flex-shrink-0 text-white/20 transition-colors group-hover:text-white/50"
                    sx={{ fontSize: 20 }}
                  />
                </div>
              </div>
            );
          })}

          <div
            className={`relative rounded-xl border border-dashed p-6 transition-all duration-150 ${
              isDemo
                ? 'cursor-not-allowed border-white/[0.06] opacity-40'
                : 'cursor-pointer border-white/[0.12] hover:border-white/25 hover:bg-neutral-800/40'
            }`}
            onClick={isDemo ? undefined : handleCreateOrg}
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03]">
                <AddIcon className="text-white/40" sx={{ fontSize: 22 }} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-white/70">New Organization</h3>
                <p className="mt-1 text-sm text-white/30">Create a team workspace</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!showLoading && !showError && organizations.length === 0 && (
        <div className="py-10 text-center text-muted-foreground">
          <BusinessIcon className="mx-auto mb-3 h-10 w-10 opacity-40 text-muted-foreground" />
          <p className="text-sm">Create an organization to collaborate with your team</p>
        </div>
      )}

      {showCreateModal && (
        <CreateOrgModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateOrgSubmit}
        />
      )}
    </div>
  );
};

export default OrganizationsList;
